import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";

const GOLD_LIGHT = "#F5DE8C";
const GOLD = "#D4AF37";
const GOLD_DARK = "#8C6510";

function formatDetail(detail) {
  if (!detail) return "Erro desconhecido";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function AuthEmailForm({ mode = "register" }) {
  const isRegister = mode === "register";
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login-email";
      const payload = isRegister ? { name, email, password } : { email, password };
      const { data } = await api.post(endpoint, payload);
      // Salva token no localStorage (compat com axios interceptor)
      if (data?.session_token) {
        try { localStorage.setItem("session_token", data.session_token); } catch {}
      }
      toast.success(isRegister ? "Conta criada com sucesso" : "Login bem-sucedido");
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden flex items-center md:justify-end justify-center px-4 md:px-16"
         style={{ background: "#050810" }} data-testid="auth-email-page">
      <img src="/hermes-hero.jpg" alt="" aria-hidden="true" draggable={false}
           className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"/>
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "linear-gradient(90deg, rgba(5,8,16,0.15) 0%, rgba(5,8,16,0.20) 40%, rgba(5,8,16,0.55) 100%)" }}/>

      <form onSubmit={submit}
        className="hermes-login-card relative w-full max-w-[440px] rounded-2xl px-6 py-8 md:px-8 md:py-9"
        data-testid="auth-email-card">

        <Link to="/" data-testid="back-login"
              className="text-neutral-400 hover:text-white text-xs inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar
        </Link>

        <div className="flex items-center justify-center gap-3 mb-5">
          <img src="/hermes-wing.png" alt="Hermes" draggable={false}
               className="h-10 w-auto object-contain"
               style={{ filter: "drop-shadow(0 4px 12px rgba(212,175,55,0.35))" }}/>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-2xl font-bold tracking-tight"
              style={{
                background: `linear-gradient(180deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, ${GOLD_DARK} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>HERMES</span>
            <span className="text-[9px] tracking-[0.22em] uppercase mt-0.5"
                  style={{ color: "rgba(230,200,102,0.85)" }}>
              Tecnologia Jurídica
            </span>
          </div>
        </div>

        <h1 className="text-center font-heading text-2xl font-semibold text-white">
          {isRegister ? "Criar conta" : "Entrar com e-mail"}
        </h1>
        <p className="text-center text-[13px] text-neutral-300/85 mt-1">
          {isRegister ? "Comece agora, é grátis para experimentar." : "Bem-vindo(a) de volta."}
        </p>

        <div className="mt-6 space-y-3">
          {isRegister && (
            <Field icon={UserIcon} placeholder="Nome completo" value={name}
                   onChange={setName} testid="input-name" type="text"/>
          )}
          <Field icon={Mail} placeholder="seu@e-mail.com" value={email}
                 onChange={setEmail} testid="input-email" type="email" required/>
          <Field icon={Lock} placeholder="Senha (mínimo 8 caracteres)" value={password}
                 onChange={setPassword} testid="input-password" type="password" required/>
        </div>

        <button type="submit" disabled={busy}
          data-testid="btn-submit-email"
          className="hermes-cta-primary group mt-5 w-full h-12 rounded-xl inline-flex items-center justify-center gap-2 text-black font-semibold cursor-pointer disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> :
                  <>{isRegister ? "Criar minha conta" : "Entrar"} <ArrowRight className="w-4 h-4"/></>}
        </button>

        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(230,200,102,0.35), transparent)" }}/>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "rgba(230,200,102,0.88)" }}>
            <ShieldCheck className="w-3.5 h-3.5"/> Acesso seguro
          </div>
          <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(230,200,102,0.35), transparent)" }}/>
        </div>

        <div className="mt-4 text-center text-[12.5px] text-neutral-400">
          {isRegister ? (
            <>Já tem conta? <Link to="/login-email" data-testid="switch-login" className="hover:text-[#F5DE8C]" style={{ color: GOLD_LIGHT }}>Entrar</Link></>
          ) : (
            <>É novo por aqui? <Link to="/criar-conta" data-testid="switch-register" className="hover:text-[#F5DE8C]" style={{ color: GOLD_LIGHT }}>Criar conta</Link></>
          )}
        </div>

        {!isRegister && (
          <div className="mt-2 text-center text-[12px]">
            <Link to="/esqueci-senha" data-testid="link-forgot" className="text-neutral-500 hover:text-[#F5DE8C]">
              Esqueci minha senha
            </Link>
          </div>
        )}

        <div className="mt-3 text-center text-[11px] text-neutral-500">
          Prefere Google? <Link to="/" className="underline decoration-dotted hover:text-neutral-300">Voltar ao login OAuth</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ icon: Icon, placeholder, value, onChange, testid, type = "text", required = false }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} type={type} required={required}
        data-testid={testid}
        className="w-full h-11 bg-black/30 text-white rounded-lg pl-9 pr-3 text-sm placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]"
        style={{ border: "1px solid rgba(230,200,102,0.30)" }}/>
    </div>
  );
}
